import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, isDevMode, OnInit, signal, OnDestroy, DestroyRef, AfterViewInit } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, Sort } from '@angular/material/sort';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { select, Store } from '@ngxs/store';
import { combineLatest, defer, distinctUntilChanged, firstValueFrom, from, map, Observable, shareReplay, switchMap } from 'rxjs';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { SubjectEntityService } from '../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../subject/subject.entity';
import { EntityStateModel, InvalidateLoadedQueries } from '../../core/state/entity.state';
import { FeedbackEntity, FeedbackTryEntry } from '../feedback.entity';
import { FeedbackEntityService } from '../services/feedback.entity.service';
import { FreeFilterInputComponent } from '../../utils/component/free-filter-input/free-filter-input.component';
import { ConfirmDialogRawComponent, SimpleConfirmData } from '../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { LocalStorageService } from '../../core/services/local-storage.service';
import { EditFeedbackComponent, FeedbackDialogData } from '../edit-feedback/edit-feedback.component';
import { TableLoadingSpinnerComponent } from '../../utils/component/table-loading-spinner/table-loading-spinner';
import { WidgetMessageService } from '../../core/services/widget-message.service';
import { AppSelectors } from '../../app.state';
import { FetchOptionsState } from '../../core/state/fetchOptions.state';
import { FetchOptionsActions } from '../../core/state/fetchOptions.actions';
import { BaseFilterComponent } from '../../utils/component/base-filter-component/base-filter-component';

class FeedbackRow {
  id = '';
  subjectId = '';
  subjectName = '';
  firstTry: FeedbackTryEntry[] = [];
  secondTry: FeedbackTryEntry[] = [];
  motivating: string[] = [];
  updated: Date | null = null;
  changedBy = '';
  version = 0;
}

const germanCollator = new Intl.Collator('de-DE', {
  sensitivity: 'base',
  numeric: true,
  usage: 'sort',
});

@Component({
  selector: 'pixl-feedback',
  imports: [
    AsyncPipe,
    DatePipe,
    MatButton,
    MatIcon,
    MatIconButton,
    MatDialogModule,
    MatPaginator,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    TranslocoModule,
    FreeFilterInputComponent,
    TableLoadingSpinnerComponent
  ],
  templateUrl: './feedback.component.html',
  styleUrl: './feedback.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainFeedbackComponent extends BaseFilterComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly store = inject(Store);
  private readonly feedbackService = inject(FeedbackEntityService);
  private readonly subjectService = inject(SubjectEntityService);
  private widgetService = inject(WidgetMessageService);
  private readonly dialog = inject(MatDialog);
  private readonly translocoService = inject(TranslocoService);

  private readonly mutationSignal = signal<number>(0);
  private readonly sortSignal = signal<number>(0);

  private readonly pageChange$ = toObservable(this.pageChange);
  private readonly freeFilter$ = toObservable(this.freeFilterChange);
  private readonly mutationRefresh$ = toObservable(this.mutationSignal);
  private readonly sortRefresh$ = toObservable(this.sortSignal);

  private _liveAnnouncer = inject(LiveAnnouncer);
  private localStorageService = inject(LocalStorageService);

  public subjects$!: Observable<SubjectEntity[]>;
  public feedbackRows$!: Observable<FeedbackRow[]>;
  public freeSubjects = signal<SubjectEntity[]>([]);
  public readonly displayedColumns: string[] = [
    'actions',
    'subject',
    'updated',
    'changedBy',
  ];

  public totalItems = 0;
  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;
  private appState$ = this.store.select(AppSelectors.appState);
  public authState = select(AppSelectors.authState);
  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.store.dispatch(new InvalidateLoadedQueries());
    this.loadRelationData();
    this.updateFreeFeedbackList();

    this.feedbackRows$ = combineLatest([
      this.pageChange$,
      this.subjects$,
      this.mutationRefresh$,
      this.sortRefresh$,
      this.freeFilter$,
    ]).pipe(
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      switchMap(([page, currentSubjects, , , freeFilter]) => {
        let sortFieldName = this.store.selectSnapshot(FetchOptionsState.getSortFieldName);
        let sortDirection = this.store.selectSnapshot(FetchOptionsState.getSortDirection);

        if (!sortFieldName) {
          sortFieldName = 'subject';
          sortDirection = 'asc';
        }

        let sortParam = sortFieldName;
        if (sortDirection === 'desc') {
          sortParam = `-${sortFieldName}`;
        }

        const query = {
          sort: sortParam,
        };

        return defer(() => this.feedbackService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.feedbackService.allByQuery(query))),
          map((feedbackItems: FeedbackEntity[]) => {
            const subjectsById = new Map<string, SubjectEntity>(
              (currentSubjects as SubjectEntity[]).map((subject: SubjectEntity) => [subject.treeId ?? subject.id, subject])
            );

            const rows = feedbackItems
              .map((feedback) => {
                const subject = subjectsById.get(feedback.subjectId);

                if (subject?.root !== true || subject.active !== true) {
                  return null;
                }

                const row = new FeedbackRow();
                row.id = feedback.id;
                row.subjectId = feedback.subjectId;
                row.subjectName = subject?.name ?? feedback.subjectId;
                row.firstTry = feedback.firstTry ?? [];
                row.secondTry = feedback.secondTry ?? [];
                row.motivating = feedback.motivating ?? [];
                row.updated = feedback.updated ? new Date(feedback.updated) : null;
                row.changedBy = feedback.changedBy ?? '';
                row.version = feedback.version ?? 0;

                return row;
              })
              .filter((row): row is FeedbackRow => row !== null)
              .filter((row) => {
                const filter = freeFilter.trim().toLowerCase();

                if (filter.length < this.freeFilterLen) {
                  return true;
                }

                return row.subjectName.toLowerCase().includes(filter) || row.subjectId.toLowerCase().includes(filter);
              });

            rows.sort((left, right) => {
              let comp = 0;

              if (sortFieldName === 'subject' || sortFieldName === 'subjectId') {
                comp = germanCollator.compare(left.subjectName, right.subjectName);
              }
              else if (sortFieldName === 'updated') {
                const timeA = left.updated ? left.updated.getTime() : 0;
                const timeB = right.updated ? right.updated.getTime() : 0;
                comp = timeA - timeB;
              }
              else if (sortFieldName === 'changedBy') {
                comp = germanCollator.compare(left.changedBy, right.changedBy);
              }

              return sortDirection === 'desc' ? -comp : comp;
            });

            this.totalItems = rows.length;

            const start = page.pageIndex * page.pageSize;
            return rows.slice(start, start + page.pageSize);
          }),
          shareReplay(1)
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.paginator().page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange.set({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  ngOnDestroy(): void {
    this.store.dispatch(new InvalidateLoadedQueries());
  }

  private sortData(sort: Sort): void {
    this.store.dispatch(new FetchOptionsActions.UpdateSortFieldName(sort.active));
    this.store.dispatch(new FetchOptionsActions.UpdateSortDirection(sort.direction));
    this.sortSignal.update(v => v + 1);
  }

  public announceSortChange(sortState: Sort): void {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    }
    else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
    this.sortData(sortState);
  }

  private updateFreeFeedbackList(): void {
    combineLatest([
      this.appState$,
      this.feedbackService.ensureLoaded()
    ]).pipe(
      map(([{ isLoaded, subjects }]) => {
        if (!isLoaded) return [];

        const allSubjects$: SubjectEntity[] = subjects;
        const state = this.store.selectSnapshot(this.feedbackService.stateClass) as EntityStateModel<FeedbackEntity>;
        const allFeedbacks = state?.entities ? Object.values(state.entities) : [];
        const existingFeedbackSubjectIds = new Set(allFeedbacks.map(f => String(f.subjectId)));

        return allSubjects$
          .filter(subject => {
            const subjectId = String(subject.treeId ?? subject.id);
            return !existingFeedbackSubjectIds.has(subjectId);
          })
          .sort((left, right) => germanCollator.compare(left.name ?? '', right.name ?? ''));
      })
    ).subscribe(subjectsWithoutFeedback => {
      this.freeSubjects.set(subjectsWithoutFeedback);
      if (isDevMode()) {
        console.log('Fächer ohne Rückmeldungen (freeSubjects, alphabetisch):', this.freeSubjects());
      }
    });
  }

  private loadRelationData(): void {
    const subjectQuery = {
      sort: 'name',
    };

    this.subjects$ = from(
      this.subjectService.ensureLoaded(subjectQuery)
    ).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery))
      ),
      shareReplay(1)
    );
  }

  public async addFeedback(): Promise<void> {
    await this.saveFeedbackFromDialog({
      freeSubjects: this.freeSubjects(),
      subjectId: '',
      firstTry: [],
      secondTry: [],
      motivating: [],
      changedBy: '',
      version: 1,
    }, true);
  }

  public async editFeedback(feedback: FeedbackRow): Promise<void> {
    this.currentSelectedRow.set(feedback.id);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { subjectName: _subjectName, ...feedbackEntityData } = feedback;
    await this.saveFeedbackFromDialog(Object.assign(new FeedbackEntity(), feedbackEntityData), false);
  }

  private async saveFeedbackFromDialog(data: FeedbackDialogData, isNew: boolean): Promise<void> {
    const dialogRef = this.dialog.open(EditFeedbackComponent, {
      minWidth: '65vw',
      data,
    });

    const updated = await firstValueFrom(dialogRef.afterClosed());

    if (!updated) {
      return;
    }

    const feedbackEntityData = this.stripFeedbackDialogMetadata(updated);

    if (!feedbackEntityData.subjectId) {
      return;
    }

    let entityToPersist: FeedbackEntity;

    if (isNew) {
      entityToPersist = {
        ...feedbackEntityData,
        updated: new Date().toISOString(),
        changedBy: this.authState().editor?.email ?? feedbackEntityData.changedBy,
      } as unknown as FeedbackEntity;
    }
    else {
      entityToPersist = {
        ...feedbackEntityData,
        version: (feedbackEntityData.version || 0) + 1,
        changedBy: this.authState().editor?.email ?? feedbackEntityData.changedBy,
        updated: new Date(),
      } as unknown as FeedbackEntity;
    }

    try {
      if (isNew) {
        await this.feedbackService.create(entityToPersist as FeedbackEntity);
      }
      else {
        await firstValueFrom(this.feedbackService.update(entityToPersist as FeedbackEntity));
      }

      this.store.dispatch(new InvalidateLoadedQueries());
      this.mutationSignal.update(v => v + 1);
      this.updateFreeFeedbackList();

      this.widgetService.setMessage({
        message: this.translocoService.translate('data_saving_successful'),
        messageTye: 'mtSuccess'
      });
    }
    catch (error) {
      console.error('Speichern fehlgeschlagen', error);
      this.widgetService.setMessage({
        message: this.translocoService.translate('data_saving_error'),
        messageTye: 'mtError'
      });
    }
  }

  private stripFeedbackDialogMetadata(updated: FeedbackEntity): FeedbackEntity {
    const feedbackEntityData = {
      ...updated,
    } as FeedbackEntity & Record<string, unknown>;

    delete feedbackEntityData.freeSubjects;
    delete feedbackEntityData.subjectName;

    return feedbackEntityData as FeedbackEntity;
  }

  public async deleteFeedback(feedback: FeedbackRow): Promise<void> {
    if (!feedback?.id) {
      return;
    }

    this.currentSelectedRow.set(feedback.id);

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: feedback.subjectName,
            headerText: this.translocoService.translate('delete_record_hint'),
            afterNameText: 'wirklich löschen?',
          }
        })
        .afterClosed()
    );

    if (!confirmed) {
      return;
    }

    await firstValueFrom(this.feedbackService.delete(feedback.id));
    this.store.dispatch(new InvalidateLoadedQueries());
    this.mutationSignal.update(v => v + 1);
    this.updateFreeFeedbackList();
  }

  trackById(_index: number, row: FeedbackRow): string {
    return row.id;
  }
}
